import React, { useEffect, useState } from "react";
import { getSf009AddedByDisplay } from "./sf009AddedByDisplay";
import { toast } from "react-toastify";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { getRawSf009FormData } from "./sf009FormData";
import {
  formatSf009DateDisplay,
  formatSf009DateDisplayOrDash,
  parseSf009DateForPicker,
  datePickerToSf009DateString,
} from "./sf009ImportDateUtils";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./horizonTable.css";
import "../forms/formTitle.css";
import { applyFooterAllPages } from "../../utils/formPdfHelpers.js";
import onsiteproLogo from "../../assets/logo2 2.png";
import AdminTaylorImportModal, { adminIsPrimaryAdmin } from "./AdminTaylorImportModal";
import { parseSiteRegisterDate } from "./adminEditModalTheme";
import { ensureFormUuid } from "../../utils/formUuid";
import {
  buildSf009SharePointFileName,
  createSf009SharePointUploadConfig,
  getSf009SharePointFields,
  postSf009PdfToSharePoint,
} from "./sf009SharePointUpload";
import { drawSignatureImageInPdfCell, isSigImage, toSigDataUrl } from "./sf009FormSignatureUtils";

import { canEditDeleteRecords } from "../../utils/rolePermissions";
const THEME = "#8b1634";
const THEME_RGB = [139, 22, 52];
const THEME_LIGHT_RGB = [239, 226, 231];
const THEME_BORDER = "rgba(139, 22, 52, 0.18)";
const PDF_WHITE = [255, 255, 255];
const PDF_PAGE_BOTTOM = 32;

const GUIDANCE_SECTIONS = [
  { type: "title", text: "Guidance for completion of risk assessment schedule" },
  {
    type: "paragraph",
    text: "This form has been developed to assist Site Managers in the systematic identification of potentially hazardous activities/processes to be undertaken on their respective sites and to plan a programme of detailed risk assessments where appropriate. The completion of the schedule and its periodic review and update are important steps in satisfying our legal obligations to carry out suitable and sufficient risk assessments in accordance with the requirements of the Management of Health and Safety at Work Regulations 1999.",
  },
  {
    type: "paragraph",
    text: "When completing this risk assessment schedule the Site Manager should adopt the following sequence and complete the schedule using the appropriate headings.",
  },
  { type: "heading", text: "Activity/process or area" },
  {
    type: "paragraph",
    text: "This column should list the activities planned are to be undertaken during the next 2/3 months and with which there may be foreseeable hazards associated. The matrix on the back page gives an indication of the types of activities that need to be considered and the hazards that may be encountered.",
  },
  { type: "heading", text: "Activity carried out by" },
  {
    type: "paragraph",
    text: 'The organisation conducting the activity and therefore responsible for conducting a "suitable and sufficient" risk assessment "specific to the task", should be identified.',
  },
  { type: "heading", text: "Specific assessment to be carried out by" },
  {
    type: "paragraph",
    text: "To identify the competent person or organisation responsible for carrying out the risk assessment. For an activity executed directly by TTSL, the names of the competent risk assessors should be entered, for an activity conducted by a contractor then the contractor's nominated assessor should be entered.",
  },
  { type: "heading", text: "Date assessment to be completed" },
  {
    type: "paragraph",
    text: "This should be used to record the date by which the detailed risk assessments must be completed. The date entered must be prior to the commencement of the activity to enable any precautionary measures identified during the detailed risk assessment to be adequately planned and implemented.",
  },
  { type: "heading", text: "Duration of activity" },
  {
    type: "paragraph",
    text: "Risk assessments for activities that continue for longer than a month from the date of issue will be reviewed. The programmed duration for specific activities must be entered.",
  },
  { type: "heading", text: "Date assessment to be reviewed" },
  {
    type: "paragraph",
    text: "This should be used to record the review date of the assessment and the names of the nominated reviewers (for TTSL assessments, these must be the original assessors. For contractors, this must be the contractors named assessors).",
  },
  { type: "heading", text: "Revision no and date" },
  {
    type: "paragraph",
    text: "This should be used to record the revision number if this is required following the review process.",
  },
  { type: "heading", text: "Close out date" },
  {
    type: "paragraph",
    text: "This should be used as a closed out date for risk assessments.",
  },
  {
    type: "paragraph",
    text: "Having identified the potentially hazardous activities and listed them on the schedule it will be necessary to ensure the preparation of the relevant risk assessment. For risk assessments conducted by TTSL personnel use the TTSL health and safety risk assessment pro forma. Contractors must use their own, but may use the TTSL pro forma if it is considered to be appropriate.",
  },
];

const BRIEFING_SECTIONS = [
  {
    type: "paragraph",
    text: "Upon completion the risk assessment the control measures contained on it must be briefed to the relevant workforce using the safety briefing sheet. Contractors may use their own briefing sheets. The signatures of those attending (TTSL personnel) the safety briefing must be recorded on the TTSL Briefing sheet, contractors briefings must record signatures of the workforce briefed.",
  },
  {
    type: "paragraph",
    text: "The safety briefing sheet of both TTSL and subcontractors should be copied and issued along with the risk assessment, to those carrying out the job and/or displayed at the workplace. The original safety briefing sheet and risk assessment must be retained in the risk assessment file.",
  },
];

function guidanceSectionPdfStyle(section) {
  if (section.type === "title") {
    return { fontStyle: "bold", fontSize: 7.5, valign: "top", cellPadding: { top: 3, bottom: 2, left: 2.5, right: 2.5 } };
  }
  if (section.type === "heading") {
    return { fontStyle: "bold", fontSize: 6.5, valign: "top", cellPadding: { top: 2, bottom: 1, left: 2.5, right: 2.5 } };
  }
  return { fontSize: 6.5, valign: "top", cellPadding: { top: 1, bottom: 2, left: 2.5, right: 2.5 } };
}

function GuidanceContent({ sections }) {
  return (
    <>
      {sections.map((section, i) => {
        if (section.type === "title") {
          return (
            <div key={i} style={{ fontWeight: 700, fontSize: 12, marginBottom: 10, color: "#1a202c" }}>
              {section.text}
            </div>
          );
        }
        if (section.type === "heading") {
          return (
            <div key={i} style={{ fontWeight: 700, fontSize: 11, marginTop: 10, marginBottom: 4, color: "#1a202c" }}>
              {section.text}
            </div>
          );
        }
        return (
          <p key={i} style={{ margin: "0 0 10px", fontSize: 11, lineHeight: 1.5, color: "#2d3748" }}>
            {section.text}
          </p>
        );
      })}
    </>
  );
}

function normalizeHeaderSignature(val) {
  if (!isSigImage(val)) return "";
  const s = String(val).trim();
  return s.startsWith("data:image") ? s : `data:image/png;base64,${s}`;
}

function emptyScheduleRow() {
  return {
    riskAssessmentNo: "",
    activityProcessOrArea: "",
    activityCarriedOutBy: "",
    specificAssessmentCarriedOutBy: "",
    dateAssessmentToBeCompleted: "",
    durationOfActivity: "",
    dateAssessmentToBeReviewed: "",
    revisionNoAndDate: "",
    closeOutDate: "",
  };
}

function getBaseUrl() {
  const apiUrl = process.env.REACT_APP_API_URL;
  return apiUrl?.endsWith("/") ? apiUrl.slice(0, -1) : apiUrl || "";
}

function normalizeScheduleRow(src) {
  const row = src && typeof src === "object" ? src : {};
  return {
    riskAssessmentNo: row.riskAssessmentNo ?? row.risk_assessment_no ?? "",
    activityProcessOrArea:
      row.activityProcessOrArea ?? row.activity_process_or_area ?? row.activityProcessArea ?? "",
    activityCarriedOutBy: row.activityCarriedOutBy ?? row.activity_carried_out_by ?? "",
    specificAssessmentCarriedOutBy:
      row.specificAssessmentCarriedOutBy ?? row.specific_assessment_carried_out_by ?? "",
    dateAssessmentToBeCompleted:
      row.dateAssessmentToBeCompleted ?? row.date_assessment_to_be_completed ?? "",
    durationOfActivity: row.durationOfActivity ?? row.duration_of_activity ?? "",
    dateAssessmentToBeReviewed: row.dateAssessmentToBeReviewed ?? row.date_assessment_to_be_reviewed ?? "",
    revisionNoAndDate: row.revisionNoAndDate ?? row.revision_no_and_date ?? "",
    closeOutDate: row.closeOutDate ?? row.close_out_date ?? "",
  };
}

function hasScheduleRowData(row) {
  if (!row || typeof row !== "object") return false;
  return Object.values(row).some((v) => v != null && String(v).trim() !== "");
}

function scheduleEntriesFromRaw(entries) {
  const list = Array.isArray(entries) ? entries.map(normalizeScheduleRow) : [];
  return list.filter(hasScheduleRowData);
}

function cloneScheduleEntries(entries) {
  return Array.isArray(entries) ? entries.map(normalizeScheduleRow) : [];
}

function mergeRecordIntoDefaults(raw) {
  const src = raw && typeof raw === "object" ? raw : {};
  const hd = src.headerDetails || {};
  return {
    siteAddressAndContractNo:
      src.siteAddressAndContractNo ??
      src.siteAddressContractNo ??
      hd.siteAddressAndContractNo ??
      src.siteAddress ??
      "",
    scheduleNumber: src.scheduleNumber ?? hd.scheduleNumber ?? "",
    preparedByName: src.preparedByName ?? hd.preparedByName ?? "",
    signature: normalizeHeaderSignature(src.signature ?? src.preparedBySignature),
    preparedDate: src.preparedDate ?? hd.preparedDate ?? src.date ?? "",
    scheduleEntries: scheduleEntriesFromRaw(
      src.scheduleEntries ?? src.entries ?? src.riskAssessmentEntries
    ),
  };
}

const sf009TblBorder = `1px solid ${THEME_BORDER}`;
const sf009LblCell = {
  border: sf009TblBorder,
  padding: "8px 10px",
  fontSize: 12,
  fontWeight: 600,
  background: THEME,
  color: "#fff",
  verticalAlign: "middle",
  whiteSpace: "nowrap",
};
const sf009ValCell = {
  border: sf009TblBorder,
  padding: "6px 8px",
  fontSize: 12,
  background: "#fff",
  verticalAlign: "middle",
};
const sf009RegHeadCell = {
  border: sf009TblBorder,
  padding: "6px 8px",
  fontSize: 9,
  fontWeight: 600,
  background: "rgb(239, 226, 231)",
  color: THEME,
  textAlign: "center",
  verticalAlign: "middle",
};
const sf009RegCell = {
  border: sf009TblBorder,
  padding: "4px 6px",
  fontSize: 11,
  background: "#fff",
  verticalAlign: "middle",
};
const sf009CellInput = {
  width: "100%",
  border: "none",
  outline: "none",
  fontSize: 11,
  padding: "4px 2px",
  background: "transparent",
  boxSizing: "border-box",
};
const sf009CellTextarea = {
  ...sf009CellInput,
  resize: "vertical",
  minHeight: 56,
  fontFamily: "inherit",
};
const sf009GuidanceCell = {
  border: sf009TblBorder,
  padding: "12px 14px",
  background: "#fff",
  verticalAlign: "top",
};
const sf009SigCell = {
  ...sf009ValCell,
  textAlign: "center",
  verticalAlign: "middle",
};

function HeaderSignatureDisplay({ value }) {
  const url = normalizeHeaderSignature(value);
  if (!url) return <span style={{ color: "#a0aec0", fontSize: 10 }}>—</span>;
  return (
    <img
      src={url}
      alt="Signature"
      style={{ maxWidth: "100%", maxHeight: 52, objectFit: "contain", display: "block", margin: "0 auto" }}
      onError={(e) => {
        e.target.style.display = "none";
      }}
    />
  );
}

function Sf009JRiskAssessmentSchedulePage() {
  const canEditDelete = canEditDeleteRecords();
  const [forms, setForms] = useState([]);
  const [loadingRecords, setLoadingRecords] = useState(true);
  const [appUsers, setAppUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const isAdminUser = adminIsPrimaryAdmin();
  const [editForm, setEditForm] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [uploadingKey, setUploadingKey] = useState(null);
  const [headerSignatureLock, setHeaderSignatureLock] = useState(null);

  useEffect(() => {
    fetchForms();
    fetchAppUsers();
  }, []);

  const getRawFormData = getRawSf009FormData;

  async function fetchForms() {
    setLoadingRecords(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/sf-009-j-risk-assessment-schedule", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setForms(res.ok && data.success && Array.isArray(data.data) ? data.data : []);
    } catch {
      setForms([]);
    } finally {
      setLoadingRecords(false);
    }
  }

  async function fetchAppUsers() {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/app-users", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setAppUsers(res.ok && data.success && Array.isArray(data.data) ? data.data : []);
    } catch {
      setAppUsers([]);
    }
  }

  const filtered = forms.filter((f) => {
    const fd = mergeRecordIntoDefaults(getRawFormData(f));
    const fields = [fd.scheduleNumber, fd.siteAddressAndContractNo, fd.preparedByName, f.name].map((v) =>
      (v || "").toLowerCase()
    );
    const words = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return words.length === 0 || words.every((w) => fields.some((field) => field.includes(w)));
  });
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paged = filtered.slice((page - 1) * pageSize, page * pageSize);

  const updateFormData = (section, key, value) => {
    setEditForm((p) => {
      const next = { ...p, formData: { ...p.formData } };
      if (section === "_flat") {
        next.formData[key] = value;
      } else if (section && key) {
        next.formData[section] = { ...(next.formData[section] || {}), [key]: value };
      }
      const fd = next.formData;
      next.name = fd.preparedByName || "";
      next.date = fd.preparedDate || fd.scheduleEntries?.[0]?.dateAssessmentToBeCompleted || "";
      return next;
    });
  };

  const updateScheduleRow = (rowIndex, field, value) => {
    setEditForm((p) => {
      const next = { ...p, formData: { ...p.formData } };
      const entries = cloneScheduleEntries(next.formData.scheduleEntries);
      entries[rowIndex] = { ...entries[rowIndex], [field]: value };
      next.formData.scheduleEntries = entries;
      next.name = next.formData.preparedByName || "";
      const firstDate = entries.find((e) => e.dateAssessmentToBeCompleted)?.dateAssessmentToBeCompleted;
      next.date = next.formData.preparedDate || firstDate || "";
      return next;
    });
  };

  const addScheduleRow = () => {
    setEditForm((p) => {
      const next = { ...p, formData: { ...p.formData } };
      next.formData.scheduleEntries = [...cloneScheduleEntries(next.formData.scheduleEntries), emptyScheduleRow()];
      return next;
    });
  };

  const removeScheduleRow = (rowIndex) => {
    setEditForm((p) => {
      const next = { ...p, formData: { ...p.formData } };
      const entries = cloneScheduleEntries(next.formData.scheduleEntries);
      entries.splice(rowIndex, 1);
      next.formData.scheduleEntries = entries;
      return next;
    });
  };

  const handleEdit = (f) => {
    const raw = getRawFormData(f);
    const merged = mergeRecordIntoDefaults(raw);
    setHeaderSignatureLock(normalizeHeaderSignature(merged.signature));
    setEditForm({
      ...f,
      formUuid: ensureFormUuid(f?.formUuid),
      formData: merged,
      name: merged.preparedByName || f.name || "",
      date: merged.preparedDate || "",
    });
    setShowEditModal(true);
  };

  const handleEditSave = async () => {
    if (!editForm?._id) return;
    try {
      const token = localStorage.getItem("token");
      const raw = editForm.formData || {};
      const entriesToSave = scheduleEntriesFromRaw(raw.scheduleEntries);
      const signatureToSave = headerSignatureLock || normalizeHeaderSignature(raw.signature);
      const res = await fetch(getBaseUrl() + "/api/sf-009-j-risk-assessment-schedule/" + editForm._id, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          ...raw,
          signature: signatureToSave,
          scheduleEntries: entriesToSave,
          name: editForm.name || raw.preparedByName,
          date: editForm.date || raw.preparedDate || entriesToSave[0]?.dateAssessmentToBeCompleted,
          formUuid: ensureFormUuid(editForm.formUuid),
        }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        await fetchForms();
        setShowEditModal(false);
        setEditForm(null);
        setHeaderSignatureLock(null);
        toast.success("Form updated!");
      } else {
        toast.error(data.message || "Failed to update");
      }
    } catch {
      toast.error("Failed to update");
    }
  };

  const handleDelete = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/sf-009-j-risk-assessment-schedule/" + deleteId, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok && data.success) {
        await fetchForms();
        toast.success("Form deleted");
      } else {
        toast.error(data.message || "Failed to delete");
      }
    } catch {
      toast.error("Failed to delete");
    } finally {
      setShowDeleteModal(false);
      setDeleteId(null);
    }
  };

  const formatDate = (d) => {
    if (!d) return "—";
    const parsed = parseSf009DateForPicker(d);
    if (parsed) return parsed.toLocaleDateString("en-GB");
    try {
      return new Date(d).toLocaleDateString("en-GB");
    } catch {
      return String(d);
    }
  };

  const getUserName = (record) => getSf009AddedByDisplay(record, appUsers);

  const safeDate = parseSf009DateForPicker;

  const fd = editForm?.formData || {};

  const generatePDF = async (record, options = {}) => {
    const shouldUploadToSharePoint = options.uploadOnly === true;
    const uploadConfig = shouldUploadToSharePoint ? createSf009SharePointUploadConfig(options) : null;

    const recordRaw = getRawFormData(record);
    const raw = mergeRecordIntoDefaults(recordRaw);
    const entries = scheduleEntriesFromRaw(recordRaw.scheduleEntries);

    const doc = new jsPDF({ orientation: "landscape" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const contentX = 10;
    const contentW = pageWidth - 20;
    const pdfMargin = { left: contentX, right: contentX, bottom: PDF_PAGE_BOTTOM };
    const headerHeight = 30;

    const safePdfDate = (v) => {
      if (!v) return "";
      const d = parseSf009DateForPicker(v);
      if (!d) return String(v);
      return d.toLocaleDateString("en-GB");
    };

    doc.setFillColor(...THEME_RGB);
    doc.rect(0, 0, pageWidth, headerHeight, "F");
    try {
      const logoImg = new Image();
      await new Promise((resolve) => {
        logoImg.onload = resolve;
        logoImg.onerror = resolve;
        logoImg.src = onsiteproLogo;
      });
      doc.addImage(logoImg, "PNG", 10, 5, 65, 20);
    } catch {}

    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text("Risk Assessment Schedule", 90, 13);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text("TAYLOR TECHNOLOGY SYSTEMS LIMITED", 90, 19);

    let yPosition = headerHeight + 8;

    const pdfGrid = {
      font: "helvetica",
      fontSize: 7.5,
      cellPadding: 2.5,
      lineWidth: 0.2,
      textColor: [0, 0, 0],
      valign: "middle",
      overflow: "linebreak",
    };
    const pdfLbl = { fillColor: THEME_RGB, fontStyle: "bold", textColor: [255, 255, 255], fontSize: 7.5 };
    const pdfVal = { fillColor: PDF_WHITE, fontSize: 7.5 };
    const pdfRegHead = {
      fillColor: THEME_LIGHT_RGB,
      fontStyle: "bold",
      textColor: THEME_RGB,
      fontSize: 6,
      halign: "center",
      valign: "middle",
    };
    const pdfRegBody = { fillColor: PDF_WHITE, fontSize: 6.5, valign: "middle", minCellHeight: 10 };

    const schedColW = {
      0: contentW * 0.07,
      1: contentW * 0.17,
      2: contentW * 0.11,
      3: contentW * 0.13,
      4: contentW * 0.1,
      5: contentW * 0.09,
      6: contentW * 0.12,
      7: contentW * 0.1,
      8: contentW * 0.11,
    };

    const ensureSpace = (needed = 20) => {
      if (yPosition + needed > pageHeight - PDF_PAGE_BOTTOM) {
        doc.addPage();
        yPosition = headerHeight + 8;
      }
    };

    const addPdfTable = (tableConfig, spaceNeeded = 20) => {
      ensureSpace(spaceNeeded);
      autoTable(doc, {
        theme: "grid",
        tableWidth: contentW,
        margin: pdfMargin,
        rowPageBreak: "auto",
        ...tableConfig,
        startY: yPosition,
      });
      yPosition = doc.lastAutoTable.finalY;
    };

    const headerSig = normalizeHeaderSignature(raw.signature);
    addPdfTable(
      {
        body: [
          ["Site address and contract no:", raw.siteAddressAndContractNo || ""],
          ["Schedule number:", raw.scheduleNumber || ""],
          ["Prepared by (name):", raw.preparedByName || ""],
          ["Signature:", headerSig ? " " : ""],
          ["Date:", safePdfDate(raw.preparedDate)],
        ],
        columnStyles: {
          0: { cellWidth: contentW * 0.32, ...pdfLbl },
          1: { cellWidth: contentW * 0.68, ...pdfVal },
        },
        styles: pdfGrid,
        didDrawCell: (data) => {
          if (data.section !== "body" || data.row.index !== 3 || data.column.index !== 1) return;
          if (!headerSig) return;
          drawSignatureImageInPdfCell(doc, data, toSigDataUrl(headerSig), 1);
        },
      },
      42
    );

    GUIDANCE_SECTIONS.forEach((section) => {
      addPdfTable(
        {
          body: [[{ content: section.text, colSpan: 2, styles: guidanceSectionPdfStyle(section) }]],
          columnStyles: { 0: { cellWidth: contentW } },
          styles: { ...pdfGrid, fillColor: PDF_WHITE },
        },
        section.type === "title" ? 22 : 14
      );
    });

    addPdfTable(
      {
        body: [["Risk Assessment Schedule"]],
        styles: {
          ...pdfGrid,
          fillColor: PDF_WHITE,
          fontStyle: "bold",
          halign: "center",
          fontSize: 11,
          minCellHeight: 10,
        },
      },
      14
    );

    BRIEFING_SECTIONS.forEach((section) => {
      addPdfTable(
        {
          body: [[{ content: section.text, colSpan: 2, styles: guidanceSectionPdfStyle(section) }]],
          columnStyles: { 0: { cellWidth: contentW } },
          styles: { ...pdfGrid, fillColor: PDF_WHITE },
        },
        18
      );
    });

    const scheduleBody = entries.map((row) => [
      row.riskAssessmentNo || "",
      row.activityProcessOrArea || "",
      row.activityCarriedOutBy || "",
      row.specificAssessmentCarriedOutBy || "",
      row.dateAssessmentToBeCompleted || "",
      row.durationOfActivity || "",
      row.dateAssessmentToBeReviewed || "",
      row.revisionNoAndDate || "",
      row.closeOutDate || "",
    ]);

    ensureSpace(40);
    autoTable(doc, {
      startY: yPosition,
      theme: "grid",
      tableWidth: contentW,
      margin: pdfMargin,
      rowPageBreak: "auto",
      showHead: "firstPage",
      head: [
        [
          { content: "Risk assessment no", styles: pdfRegHead },
          { content: "Activity/ process or area (see notes on back page)", styles: pdfRegHead },
          { content: "Activity carried out by (name)", styles: pdfRegHead },
          { content: "Specific assessment to be carried out by (name)", styles: pdfRegHead },
          { content: "Date assessment to be completed", styles: pdfRegHead },
          { content: "Duration of activity", styles: pdfRegHead },
          { content: "Date assessment to be reviewed", styles: pdfRegHead },
          { content: "Revision no and date", styles: pdfRegHead },
          { content: "Close out date", styles: pdfRegHead },
        ],
      ],
      body: scheduleBody.length > 0 ? scheduleBody : [["", "", "", "", "", "", "", "", ""]],
      styles: pdfGrid,
      bodyStyles: pdfRegBody,
      headStyles: {
        fillColor: THEME_LIGHT_RGB,
        textColor: THEME_RGB,
        fontStyle: "bold",
        fontSize: 6,
        halign: "center",
        valign: "middle",
        lineWidth: 0.2,
      },
      columnStyles: schedColW,
    });
    yPosition = doc.lastAutoTable.finalY;

    applyFooterAllPages(doc);

    const displayName = raw.preparedByName || record.name || "Schedule";
    const safeSite = String(raw.siteAddressAndContractNo || raw.scheduleNumber || "Site").replace(/[\\/:"*?<>|]+/g, "-");
    const safeName = String(displayName).replace(/[\\/:"*?<>|]+/g, "-");
    const safeDateStr = String(
      formatDate(record.date || raw.preparedDate || entries[0]?.dateAssessmentToBeCompleted) || "form"
    ).replace(/[\\/:"*?<>|]+/g, "-");
    const fileName =
      shouldUploadToSharePoint && uploadConfig
        ? buildSf009SharePointFileName("J", getSf009SharePointFields("J", raw, record, getUserName))
        : `SF009J_${safeName}_${safeDateStr}.pdf`;

    if (shouldUploadToSharePoint && uploadConfig) {
      try {
        const key = `${record._id || ""}|${raw.scheduleNumber || ""}|${record.date || ""}`;
        await postSf009PdfToSharePoint({ doc, fileName, uploadConfig, setUploadingKey, uploadKey: key });
        toast.success("PDF uploaded to SharePoint successfully!");
      } catch (err) {
        toast.error(err?.message || "SharePoint upload failed");
      }
    } else {
      doc.save(fileName);
      toast.success("SF-009-J Risk Assessment Schedule PDF generated successfully!");
    }
  };

  const renderScheduleForm = () => {
    const entries = cloneScheduleEntries(fd.scheduleEntries);
    return (
      <div style={{ border: sf009TblBorder, background: "#fff" }}>
        <div
          style={{
            textAlign: "center",
            padding: "14px 12px",
            fontWeight: 700,
            fontSize: 15,
            color: "#1a202c",
            borderBottom: sf009TblBorder,
          }}
        >
          Risk Assessment Schedule
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
          <tbody>
            <tr>
              <td style={{ ...sf009LblCell, width: "32%" }}>Site address and contract no:</td>
              <td style={sf009ValCell}>
                <textarea
                  value={fd.siteAddressAndContractNo || ""}
                  onChange={(e) => updateFormData("_flat", "siteAddressAndContractNo", e.target.value)}
                  style={sf009CellTextarea}
                  rows={2}
                />
              </td>
            </tr>
            <tr>
              <td style={sf009LblCell}>Schedule number:</td>
              <td style={sf009ValCell}>
                <input
                  type="text"
                  value={fd.scheduleNumber || ""}
                  onChange={(e) => updateFormData("_flat", "scheduleNumber", e.target.value)}
                  style={sf009CellInput}
                />
              </td>
            </tr>
            <tr>
              <td style={sf009LblCell}>Prepared by (name):</td>
              <td style={sf009ValCell}>
                <input
                  type="text"
                  value={fd.preparedByName || ""}
                  onChange={(e) => updateFormData("_flat", "preparedByName", e.target.value)}
                  style={sf009CellInput}
                />
              </td>
            </tr>
            <tr>
              <td style={sf009LblCell}>Signature:</td>
              <td style={sf009SigCell}>
                <HeaderSignatureDisplay value={fd.signature} />
              </td>
            </tr>
            <tr>
              <td style={sf009LblCell}>Date:</td>
              <td style={sf009ValCell}>
                <DatePicker
                  selected={safeDate(fd.preparedDate)}
                  onChange={(d) => updateFormData("_flat", "preparedDate", d ? datePickerToSf009DateString(d) : "")}
                  dateFormat="dd-MM-yyyy"
                  className="form-control form-control-sm border-0 p-0"
                  placeholderText="dd-MM-yyyy"
                  wrapperClassName="w-100"
                />
              </td>
            </tr>
          </tbody>
        </table>
        <table style={{ width: "100%", borderCollapse: "collapse", borderTop: sf009TblBorder }}>
          <tbody>
            <tr>
              <td style={sf009GuidanceCell}>
                <GuidanceContent sections={GUIDANCE_SECTIONS} />
              </td>
            </tr>
          </tbody>
        </table>
        <div
          style={{
            textAlign: "center",
            padding: "12px 12px 8px",
            fontWeight: 700,
            fontSize: 14,
            color: "#4a5568",
            borderTop: sf009TblBorder,
            background: "#fff",
          }}
        >
          Risk Assessment Schedule
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <tbody>
            <tr>
              <td style={sf009GuidanceCell}>
                <GuidanceContent sections={BRIEFING_SECTIONS} />
              </td>
            </tr>
          </tbody>
        </table>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            padding: "10px 12px",
            borderTop: sf009TblBorder,
            background: "#fff",
          }}
        >
          <button
            type="button"
            onClick={addScheduleRow}
            style={{
              background: THEME,
              color: "#fff",
              border: "none",
              borderRadius: 8,
              padding: "8px 16px",
              fontWeight: 600,
              fontSize: 13,
              cursor: "pointer",
            }}
          >
            <i className="fa fa-plus" style={{ marginRight: 6 }} />
            Add row
          </button>
        </div>
        <div style={{ overflowX: "auto", borderTop: sf009TblBorder }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1400 }}>
            <thead>
              <tr>
                <th style={sf009RegHeadCell}>Risk assessment no</th>
                <th style={sf009RegHeadCell}>Activity/ process or area (see notes on back page)</th>
                <th style={sf009RegHeadCell}>Activity carried out by (name)</th>
                <th style={sf009RegHeadCell}>Specific assessment to be carried out by (name)</th>
                <th style={sf009RegHeadCell}>Date assessment to be completed</th>
                <th style={sf009RegHeadCell}>Duration of activity</th>
                <th style={sf009RegHeadCell}>Date assessment to be reviewed</th>
                <th style={sf009RegHeadCell}>Revision no and date</th>
                <th style={sf009RegHeadCell}>Close out date</th>
                <th style={{ ...sf009RegHeadCell, width: 40 }}>&nbsp;</th>
              </tr>
            </thead>
            <tbody>
              {entries.length === 0 ? (
                <tr>
                  <td colSpan={10} style={{ ...sf009RegCell, textAlign: "center", color: "#718096", padding: 20 }}>
                    No schedule rows yet. Click &quot;Add row&quot; to add an entry.
                  </td>
                </tr>
              ) : (
                entries.map((row, idx) => (
                  <tr key={idx}>
                    <td style={sf009RegCell}>
                      <input
                        type="text"
                        value={row.riskAssessmentNo || ""}
                        onChange={(e) => updateScheduleRow(idx, "riskAssessmentNo", e.target.value)}
                        style={sf009CellInput}
                      />
                    </td>
                    <td style={sf009RegCell}>
                      <input
                        type="text"
                        value={row.activityProcessOrArea || ""}
                        onChange={(e) => updateScheduleRow(idx, "activityProcessOrArea", e.target.value)}
                        style={sf009CellInput}
                      />
                    </td>
                    <td style={sf009RegCell}>
                      <input
                        type="text"
                        value={row.activityCarriedOutBy || ""}
                        onChange={(e) => updateScheduleRow(idx, "activityCarriedOutBy", e.target.value)}
                        style={sf009CellInput}
                      />
                    </td>
                    <td style={sf009RegCell}>
                      <input
                        type="text"
                        value={row.specificAssessmentCarriedOutBy || ""}
                        onChange={(e) => updateScheduleRow(idx, "specificAssessmentCarriedOutBy", e.target.value)}
                        style={sf009CellInput}
                      />
                    </td>
                    <td style={sf009RegCell}>
                      <input
                        type="text"
                        value={row.dateAssessmentToBeCompleted || ""}
                        onChange={(e) => updateScheduleRow(idx, "dateAssessmentToBeCompleted", e.target.value)}
                        style={sf009CellInput}
                      />
                    </td>
                    <td style={sf009RegCell}>
                      <input
                        type="text"
                        value={row.durationOfActivity || ""}
                        onChange={(e) => updateScheduleRow(idx, "durationOfActivity", e.target.value)}
                        style={sf009CellInput}
                      />
                    </td>
                    <td style={sf009RegCell}>
                      <input
                        type="text"
                        value={row.dateAssessmentToBeReviewed || ""}
                        onChange={(e) => updateScheduleRow(idx, "dateAssessmentToBeReviewed", e.target.value)}
                        style={sf009CellInput}
                      />
                    </td>
                    <td style={sf009RegCell}>
                      <input
                        type="text"
                        value={row.revisionNoAndDate || ""}
                        onChange={(e) => updateScheduleRow(idx, "revisionNoAndDate", e.target.value)}
                        style={sf009CellInput}
                      />
                    </td>
                    <td style={sf009RegCell}>
                      <input
                        type="text"
                        value={row.closeOutDate || ""}
                        onChange={(e) => updateScheduleRow(idx, "closeOutDate", e.target.value)}
                        style={sf009CellInput}
                      />
                    </td>
                    <td style={{ ...sf009RegCell, textAlign: "center" }}>
                      <button
                        type="button"
                        onClick={() => removeScheduleRow(idx)}
                        title="Remove row"
                        style={{
                          background: "transparent",
                          border: "none",
                          color: "#e53e3e",
                          cursor: "pointer",
                          padding: 4,
                        }}
                      >
                        <i className="fa fa-trash" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <>
      <div
        style={{
          background: "#fff",
          borderRadius: 20,
          boxShadow: "0px 4px 20px rgba(0,0,0,0.08)",
          padding: 24,
          marginBottom: 24,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 24,
            flexWrap: "wrap",
            gap: 16,
          }}
        >
          <h2 style={{ fontSize: 24, fontWeight: 700, color: "#1a202c", margin: 0 }}>
            SF-009-J Risk Assessment Schedule
          </h2>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            {isAdminUser && (
              <button
                type="button"
                onClick={() => setShowImportModal(true)}
                style={{
                  background: THEME,
                  color: "#fff",
                  border: "none",
                  borderRadius: 10,
                  padding: "10px 14px",
                  fontWeight: 600,
                  fontSize: 13,
                  cursor: "pointer",
                }}
              >
                Import File
              </button>
            )}
            <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
              <i
                className="fa fa-search"
                style={{ position: "absolute", left: 12, color: THEME, fontSize: 14, pointerEvents: "none", zIndex: 1 }}
              />
              <input
                type="text"
                placeholder="Type to Search"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                style={{
                  padding: "10px 12px 10px 36px",
                  borderRadius: 12,
                  border: "1px solid #e2e8f0",
                  fontSize: 14,
                  outline: "none",
                  width: 250,
                  boxSizing: "border-box",
                }}
              />
            </div>
          </div>
        </div>
        <div style={{ overflowX: "auto", borderRadius: 12, border: "1px solid #e2e8f0" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#f7fafc", borderBottom: "1px solid #e2e8f0" }}>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>
                  Schedule Number
                </th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>
                  Site Address / Contract No
                </th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>
                  Prepared By
                </th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>
                  Added By
                </th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>
                  Updated Date
                </th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {loadingRecords ? (
                <tr>
                  <td colSpan={6} style={{ textAlign: "center", padding: 40, color: THEME, fontSize: 16, fontWeight: 600 }}>
                    <i className="fa fa-spinner fa-spin" style={{ marginRight: 8 }} />
                    Loading records...
                  </td>
                </tr>
              ) : paged.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ textAlign: "center", padding: 40, color: "#718096", fontSize: 16 }}>
                    No records found.
                  </td>
                </tr>
              ) : (
                paged.map((r, idx) => {
                  const raw = mergeRecordIntoDefaults(getRawFormData(r));
                  const updatedDate = r.updatedAt || r.createdAt || r.date;
                  return (
                    <tr key={r._id || idx} style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{raw.scheduleNumber || "—"}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>
                        {raw.siteAddressAndContractNo || "—"}
                      </td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{raw.preparedByName || r.name || "—"}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{getUserName(r)}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{formatDate(updatedDate)}</td>
                      <td style={{ padding: 16 }}>
                        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                          {canEditDelete && (
                            <button
                              onClick={() => handleEdit(r)}
                              title="Edit"
                              style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6 }}
                            >
                              <i className="fa fa-edit" style={{ color: "#22c55e", fontSize: 16 }} />
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => generatePDF(r)}
                            title="Generate PDF"
                            style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6 }}
                          >
                            <i className="fa fa-file-pdf-o" style={{ color: "#dc2626", fontSize: 16 }} />
                          </button>
                          {(() => {
                            const k = `${r._id || ""}|${raw.scheduleNumber || ""}|${r.date || ""}`;
                            const busy = uploadingKey === k;
                            return (
                              <button
                                type="button"
                                onClick={() => {
                                  if (!busy) {
                                    generatePDF(r, { uploadOnly: true, onSuccess: () => {}, onError: () => {} });
                                  }
                                }}
                                disabled={busy}
                                title={busy ? "Uploading..." : "Upload to SharePoint"}
                                style={{
                                  background: "transparent",
                                  border: "none",
                                  cursor: busy ? "not-allowed" : "pointer",
                                  padding: 6,
                                  opacity: busy ? 0.6 : 1,
                                }}
                              >
                                <i
                                  className={busy ? "fa fa-spinner fa-spin" : "fa fa-upload"}
                                  style={{ color: "#22c55e", fontSize: 16 }}
                                />
                              </button>
                            );
                          })()}
                          {canEditDelete && (
                            <button
                              onClick={() => handleDelete(r._id)}
                              title="Delete"
                              style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6 }}
                            >
                              <i className="fa fa-trash" style={{ color: "#e53e3e", fontSize: 16 }} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", marginTop: 18, gap: 12 }}>
          <button
            className="horizon-table-btn"
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
            style={{
              padding: "8px 16px",
              borderRadius: 8,
              border: "1px solid #e2e8f0",
              background: page === 1 ? "#f7fafc" : "#fff",
              color: page === 1 ? "#a0aec0" : THEME,
              cursor: page === 1 ? "not-allowed" : "pointer",
            }}
          >
            Prev
          </button>
          <span style={{ fontWeight: 700, color: THEME, fontSize: 16 }}>
            {page} / {totalPages}
          </span>
          <button
            className="horizon-table-btn"
            disabled={page === totalPages}
            onClick={() => setPage(page + 1)}
            style={{
              padding: "8px 16px",
              borderRadius: 8,
              border: "1px solid #e2e8f0",
              background: page === totalPages ? "#f7fafc" : "#fff",
              color: page === totalPages ? "#a0aec0" : THEME,
              cursor: page === totalPages ? "not-allowed" : "pointer",
            }}
          >
            Next
          </button>
        </div>
      </div>

      {showEditModal && editForm && (
        <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.3)" }} tabIndex="-1">
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content" style={{ maxWidth: 1100, margin: "0 auto" }}>
              <div className="modal-header" style={{ background: THEME, color: "#fff" }}>
                <h5 className="modal-title">Edit SF-009-J Risk Assessment Schedule</h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => {
                    setShowEditModal(false);
                    setHeaderSignatureLock(null);
                  }}
                />
              </div>
              <div className="modal-body" style={{ maxHeight: "75vh", overflowY: "auto", background: "#f8fafc", padding: 16 }}>
                {renderScheduleForm()}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowEditModal(false);
                    setHeaderSignatureLock(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn"
                  style={{ background: THEME, color: "#fff", fontWeight: 600 }}
                  onClick={handleEditSave}
                >
                  Submit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}



      <AdminTaylorImportModal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        title="Upload SF-009-J Risk Assessment Schedule File"
        importPath="sf-009-j-risk-assessment-schedule/import-form"
        isAdminUser={isAdminUser}
        onImported={({ insertedRow }) => {
          if (insertedRow && insertedRow._id) {
            setForms((prev) => [insertedRow, ...prev]);
            setPage(1);
          }
        }}
      />

      {showDeleteModal && (
        <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.3)" }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Delete SF-009-J Risk Assessment Schedule</h5>
                <button type="button" className="btn-close" onClick={() => setShowDeleteModal(false)} />
              </div>
              <div className="modal-body">Are you sure you want to delete this form?</div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>
                  Cancel
                </button>
                <button type="button" className="btn btn-danger" onClick={confirmDelete}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Sf009JRiskAssessmentSchedulePage;
